const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=80'

export const FALLBACK_TYPES = {
  HERO_IMAGE: 'heroImage',
  STATIC: 'static',
  STORE_PRODUCT_IMAGE: 'storeProductImage',
}

/**
 * Template config mappings define how to map AI config API paths to template config structure.
 * 
 * Each mapping entry is a tuple: [configPath, apiPath, fallbackType?, staticFallback?, forTemplates?]
 * - configPath: dot-notation path in the template's config (e.g., 'hero.title')
 * - apiPath: dot-notation path in the AI config API response
 * - fallbackType: optional - 'heroImage' uses template's hero background, 'static' uses staticFallback
 * - staticFallback: optional - static fallback value when fallbackType is 'static'
 * - forTemplates: optional string[] — if set, this row applies only when selectedTemplate.name matches
 * 
 * Values returned from get() that are undefined/null will not be included in overrides,
 * allowing the template's defaults to be used.
 */
export const templateConfigMappings = {
  about: [
    // aboutUs1: [description, title] → hero
    ['hero.title', 'aboutus.blocks.aboutUs1.elements.0.description.content'],
    ['hero.subtitle', 'aboutus.blocks.aboutUs1.elements.1.title.content'],
    // aboutUs2: flat stream — nav titles, copy, image trio, banners, then fullName/description blocks
    ['images.aboutUs1', 'aboutus.blocks.aboutUs2.elements.6.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['images.aboutUs2', 'aboutus.blocks.aboutUs2.elements.7.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['images.aboutUs3', 'aboutus.blocks.aboutUs2.elements.8.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['images.aboutUs4', 'aboutus.blocks.aboutUs2.elements.11.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['images.aboutUs5', 'aboutus.blocks.aboutUs2.elements.15.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['images.aboutUs6', 'aboutus.blocks.aboutUs2.elements.19.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['images.aboutUs7', 'aboutus.blocks.aboutUs2.elements.22.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['sections.first.label', 'aboutus.blocks.aboutUs2.elements.0.title.content'],
    ['sections.first.description', 'aboutus.blocks.aboutUs2.elements.2.description.content'],
    ['sections.second.label', 'aboutus.blocks.aboutUs2.elements.3.title.content'],
    ['sections.second.captionTop', 'aboutus.blocks.aboutUs2.elements.5.description.content'],
    ['sections.third.label', 'aboutus.blocks.aboutUs2.elements.6.title.content'],
    ['sections.third.bannerTitle', 'aboutus.blocks.aboutUs2.elements.8.title.content'],
    ['sections.fourth.label', 'aboutus.blocks.aboutUs2.elements.9.title.content'],
    ['sections.fifth.label', 'aboutus.blocks.aboutUs2.elements.16.title.content'],
    ['sections.fifth.exclusiveTitle', 'aboutus.blocks.aboutUs2.elements.17.title.content'],
    ['textBlocks.0.title', 'aboutus.blocks.aboutUs2.elements.10.fullName.content'],
    ['textBlocks.0.subtitle', 'aboutus.blocks.aboutUs2.elements.1.title.content'],
    ['textBlocks.0.paragraph', 'aboutus.blocks.aboutUs2.elements.11.description.content'],
    ['textBlocks.1.title', 'aboutus.blocks.aboutUs2.elements.12.fullName.content'],
    ['textBlocks.1.subtitle', 'aboutus.blocks.aboutUs2.elements.4.title.content'],
    ['textBlocks.1.paragraph', 'aboutus.blocks.aboutUs2.elements.13.description.content'],
    ['textBlocks.2.title', 'aboutus.blocks.aboutUs2.elements.14.fullName.content'],
    ['textBlocks.2.subtitle', 'aboutus.blocks.aboutUs2.elements.7.title.content'],
    ['textBlocks.2.paragraph', 'aboutus.blocks.aboutUs2.elements.15.description.content'],
    ['contact.email', 'aboutus.blocks.accountInfo.email.content'],
  ],

  appointments: [
    ['appointments.0.image', 'booking.blocks.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['appointments.0.title', 'booking.blocks.elements.0.title.content'],
    ['appointments.0.description', 'booking.blocks.elements.0.shortDescription.content'],
    ['appointments.1.image', 'booking.blocks.elements.1.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['appointments.1.title', 'booking.blocks.elements.1.title.content'],
    ['appointments.1.description', 'booking.blocks.elements.1.shortDescription.content'],
  ],

  store: [
    ['products.0.title', 'store.blocks.elements.0.title.content'],
    ['products.0.image', 'store.blocks.elements.0.image.url', FALLBACK_TYPES.STORE_PRODUCT_IMAGE],
    ['products.1.title', 'store.blocks.elements.1.title.content'],
    ['products.1.image', 'store.blocks.elements.1.image.url', FALLBACK_TYPES.STORE_PRODUCT_IMAGE],
    ['products.2.title', 'store.blocks.elements.2.title.content'],
    ['products.2.image', 'store.blocks.elements.2.image.url', FALLBACK_TYPES.STORE_PRODUCT_IMAGE],
  ],

  blog: [
    ['featureImage', 'blog.blocks.blog.elements.2.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['introParagraph', 'blog.blocks.blog.elements.1.shortDescription.content'],
    ['featureImageAlt', 'blog.blocks.blog.elements.0.title.content', FALLBACK_TYPES.STATIC, 'Featured blog image'],
    ['author', 'blog.blocks.accountInfo.fullName.content', FALLBACK_TYPES.STATIC, 'Anonymous'],
  ],

  contact_form: [
    ['hero.image', 'contact.blocks.cover.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['hero.title', 'contact.blocks.cover.elements.0.title.content'],
    ['info.address.value', 'contact.blocks.accountInfo.address.content'],
    ['info.email.value', 'contact.blocks.accountInfo.email.content'],
    ['info.phone.value', 'contact.blocks.accountInfo.phone.content'],
    ['form.title', 'contact.blocks.contact.elements.1.title.content'],
  ],

  faq: [
    // Element 0 = section title, 1 = section description; then [title, description] pairs with optional icons in between
    ['title', 'faqs.blocks.faq.elements.0.title.content'],
    ['subtitle', 'faqs.blocks.faq.elements.1.description.content'],
    ['faqs.0.question', 'faqs.blocks.faq.elements.2.title.content'],
    ['faqs.0.answer', 'faqs.blocks.faq.elements.3.description.content'],
    ['faqs.1.question', 'faqs.blocks.faq.elements.4.title.content'],
    ['faqs.1.answer', 'faqs.blocks.faq.elements.5.description.content'],
    ['faqs.2.question', 'faqs.blocks.faq.elements.6.title.content'],
    ['faqs.2.answer', 'faqs.blocks.faq.elements.7.description.content'],
    ['faqs.3.question', 'faqs.blocks.faq.elements.8.title.content'],
    ['faqs.3.answer', 'faqs.blocks.faq.elements.9.description.content'],
    ['faqs.4.question', 'faqs.blocks.faq.elements.10.title.content'],
    ['faqs.4.answer', 'faqs.blocks.faq.elements.11.description.content'],
    ['faqs.5.question', 'faqs.blocks.faq.elements.12.title.content'],
    ['faqs.5.answer', 'faqs.blocks.faq.elements.13.description.content'],
  ],

  portfolio: [
    // portfolio1 = page title only; portfolio2/3/4 = sections with [title, description, image, image]
    ['mainTitle', 'portfolio.blocks.portfolio1.elements.0.title.content'],
    ['sections.0.title', 'portfolio.blocks.portfolio2.elements.0.title.content'],
    ['sections.0.paragraph', 'portfolio.blocks.portfolio2.elements.1.description.content'],
    ['sections.0.smallImage', 'portfolio.blocks.portfolio2.elements.3.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['sections.0.largeImage', 'portfolio.blocks.portfolio2.elements.2.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['sections.1.title', 'portfolio.blocks.portfolio3.elements.0.title.content'],
    ['sections.1.paragraph', 'portfolio.blocks.portfolio3.elements.1.description.content'],
    ['sections.1.smallImage', 'portfolio.blocks.portfolio3.elements.3.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['sections.1.largeImage', 'portfolio.blocks.portfolio3.elements.2.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['sections.2.title', 'portfolio.blocks.portfolio4.elements.0.title.content'],
    ['sections.2.paragraph', 'portfolio.blocks.portfolio4.elements.1.description.content'],
    ['sections.2.smallImage', 'portfolio.blocks.portfolio4.elements.3.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['sections.2.largeImage', 'portfolio.blocks.portfolio4.elements.2.image.url', FALLBACK_TYPES.HERO_IMAGE],
  ],

  team: [
    // Element 0 = page title; then repeating [fullName, subtitle, description, image] per member
    // Member N: fullName at 4N+1, subtitle (role) at 4N+2, description at 4N+3, image at 4N+4
    ['sectionTitle', 'team.blocks.team.elements.0.title.content'],
    ['teamMembers.0.name', 'team.blocks.team.elements.1.fullName.content'],
    ['teamMembers.0.role', 'team.blocks.team.elements.2.subtitle.content'],
    ['teamMembers.0.description', 'team.blocks.team.elements.3.description.content'],
    ['teamMembers.0.image', 'team.blocks.team.elements.4.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['teamMembers.1.name', 'team.blocks.team.elements.5.fullName.content'],
    ['teamMembers.1.role', 'team.blocks.team.elements.6.subtitle.content'],
    ['teamMembers.1.description', 'team.blocks.team.elements.7.description.content'],
    ['teamMembers.1.image', 'team.blocks.team.elements.8.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['teamMembers.2.name', 'team.blocks.team.elements.9.fullName.content'],
    ['teamMembers.2.role', 'team.blocks.team.elements.10.subtitle.content'],
    ['teamMembers.2.description', 'team.blocks.team.elements.11.description.content'],
    ['teamMembers.2.image', 'team.blocks.team.elements.12.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['teamMembers.3.name', 'team.blocks.team.elements.13.fullName.content'],
    ['teamMembers.3.role', 'team.blocks.team.elements.14.subtitle.content'],
    ['teamMembers.3.description', 'team.blocks.team.elements.15.description.content'],
    ['teamMembers.3.image', 'team.blocks.team.elements.16.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['teamMembers.4.name', 'team.blocks.team.elements.17.fullName.content'],
    ['teamMembers.4.role', 'team.blocks.team.elements.18.subtitle.content'],
    ['teamMembers.4.description', 'team.blocks.team.elements.19.description.content'],
    ['teamMembers.4.image', 'team.blocks.team.elements.20.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['teamMembers.5.name', 'team.blocks.team.elements.21.fullName.content'],
    ['teamMembers.5.role', 'team.blocks.team.elements.22.subtitle.content'],
    ['teamMembers.5.description', 'team.blocks.team.elements.23.description.content'],
    ['teamMembers.5.image', 'team.blocks.team.elements.24.image.url', FALLBACK_TYPES.HERO_IMAGE],
  ],

  testimonials: [
    // Element 0 is the section title, then repeating groups of [image, description, fullName]
    // Testimonial N: image at 3N+1, description (quote) at 3N+2, fullName (reviewer) at 3N+3
    ['sectionTitle', 'reviews.blocks.testimonials.elements.0.title.content'],
    ['testimonials.0.image', 'reviews.blocks.testimonials.elements.1.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.0.quote', 'reviews.blocks.testimonials.elements.2.description.content'],
    ['testimonials.0.reviewer', 'reviews.blocks.testimonials.elements.3.fullName.content'],
    ['testimonials.1.image', 'reviews.blocks.testimonials.elements.4.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.1.quote', 'reviews.blocks.testimonials.elements.5.description.content'],
    ['testimonials.1.reviewer', 'reviews.blocks.testimonials.elements.6.fullName.content'],
    ['testimonials.2.image', 'reviews.blocks.testimonials.elements.7.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.2.quote', 'reviews.blocks.testimonials.elements.8.description.content'],
    ['testimonials.2.reviewer', 'reviews.blocks.testimonials.elements.9.fullName.content'],
    ['testimonials.3.image', 'reviews.blocks.testimonials.elements.10.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.3.quote', 'reviews.blocks.testimonials.elements.11.description.content'],
    ['testimonials.3.reviewer', 'reviews.blocks.testimonials.elements.12.fullName.content'],
    ['testimonials.4.image', 'reviews.blocks.testimonials.elements.13.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.4.quote', 'reviews.blocks.testimonials.elements.14.description.content'],
    ['testimonials.4.reviewer', 'reviews.blocks.testimonials.elements.15.fullName.content'],
    ['testimonials.5.image', 'reviews.blocks.testimonials.elements.16.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.5.quote', 'reviews.blocks.testimonials.elements.17.description.content'],
    ['testimonials.5.reviewer', 'reviews.blocks.testimonials.elements.18.fullName.content'],
    ['testimonials.6.image', 'reviews.blocks.testimonials.elements.19.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.6.quote', 'reviews.blocks.testimonials.elements.20.description.content'],
    ['testimonials.6.reviewer', 'reviews.blocks.testimonials.elements.21.fullName.content'],
    ['testimonials.7.image', 'reviews.blocks.testimonials.elements.22.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.7.quote', 'reviews.blocks.testimonials.elements.23.description.content'],
    ['testimonials.7.reviewer', 'reviews.blocks.testimonials.elements.24.fullName.content'],
  ],

  header_and_introduction: [
    // === ABOUT SECTION (Law Zip, Shampoo Conditioner, Spa) ===
    ['about.title', 'home.blocks.aboutUs.elements.1.title.content'],
    ['about.description', 'home.blocks.aboutUs.elements.0.shortDescription.content'],
    ['about.image', 'home.blocks.aboutUs.elements.1.image.url', FALLBACK_TYPES.HERO_IMAGE],
    // Shampoo Conditioner specific
    ['about.bannerImage', 'home.blocks.photo.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['about.label', 'home.blocks.aboutUs.elements.0.subtitle.content'],
    // Spa specific (uses different about structure)
    ['about.subtitle', 'home.blocks.aboutUs.elements.2.shortDescription.content'],
    ['about.bullets.0', 'home.blocks.aboutUs.elements.3.shortDescription.content'],
    ['about.bullets.1', 'home.blocks.aboutUs.elements.4.shortDescription.content'],
    ['about.bullets.2', 'home.blocks.aboutUs.elements.5.shortDescription.content'],

    // === INTRO SECTION (Max Fit, Pet Sitter) ===
    ['intro.title', 'home.blocks.aboutUs.elements.0.title.content'],
    ['intro.description', 'home.blocks.aboutUs.elements.1.shortDescription.content'],
    ['intro.image', 'home.blocks.aboutUs.elements.1.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['intro.bullets.0', 'home.blocks.aboutUs.elements.2.shortDescription.content'],
    ['intro.bullets.1', 'home.blocks.aboutUs.elements.3.shortDescription.content'],
    ['intro.bullets.2', 'home.blocks.aboutUs.elements.4.shortDescription.content'],
    ['intro.bullets.3', 'home.blocks.aboutUs.elements.5.shortDescription.content'],

    // === CONTACT FORM SECTION (Max Fit, Pet Sitter) ===
    ['contactForm.title', 'home.blocks.contact.elements.0.title.content'],
    ['contactForm.subtitle', 'home.blocks.contact.elements.1.shortDescription.content'],
    ['contactForm.image', 'home.blocks.contact.elements.1.image.url', FALLBACK_TYPES.HERO_IMAGE],

    // === CONTACT SECTION (Law Zip uses contact2, Spa uses contact) ===
    ['contact.title', 'home.blocks.contact2.elements.1.title.content'],
    ['contact.subtitle', 'home.blocks.contact2.elements.0.shortDescription.content'],
    // Spa contact section
    ['contact.formTitle', 'home.blocks.contact.elements.2.title.content'],
    ['contact.address', 'home.blocks.accountInfo.address.content'],
    ['contact.phone', 'home.blocks.accountInfo.phone.content'],
    ['contact.email', 'home.blocks.accountInfo.email.content'],

    // === TESTIMONIALS SECTION (Law Zip - array of 3 items) ===
    ['testimonials.title', 'home.blocks.testimonials.elements.9.title.content'],
    ['testimonials.subtitle', 'home.blocks.testimonials.elements.10.shortDescription.content'],
    ['testimonials.items.0.name', 'home.blocks.testimonials.elements.0.fullName.content'],
    ['testimonials.items.0.role', 'home.blocks.testimonials.elements.1.title.content'],
    ['testimonials.items.0.description', 'home.blocks.testimonials.elements.2.shortDescription.content'],
    ['testimonials.items.0.quote', 'home.blocks.testimonials.elements.0.description.content'],
    ['testimonials.items.0.image', 'home.blocks.testimonials.elements.2.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.items.1.name', 'home.blocks.testimonials.elements.3.fullName.content'],
    ['testimonials.items.1.role', 'home.blocks.testimonials.elements.4.title.content'],
    ['testimonials.items.1.description', 'home.blocks.testimonials.elements.5.shortDescription.content'],
    ['testimonials.items.1.quote', 'home.blocks.testimonials.elements.3.description.content'],
    ['testimonials.items.1.image', 'home.blocks.testimonials.elements.8.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['testimonials.items.2.name', 'home.blocks.testimonials.elements.6.fullName.content'],
    ['testimonials.items.2.role', 'home.blocks.testimonials.elements.7.title.content'],
    ['testimonials.items.2.description', 'home.blocks.testimonials.elements.8.shortDescription.content'],
    ['testimonials.items.2.quote', 'home.blocks.testimonials.elements.5.description.content'],
    ['testimonials.items.2.image', 'home.blocks.testimonials.elements.5.image.url', FALLBACK_TYPES.HERO_IMAGE],

    // === TESTIMONIALS REVIEWS (Pet Sitter uses 'reviews' instead of 'items') ===
    ['testimonials.reviews.0.quote', 'home.blocks.testimonials.elements.0.shortDescription.content'],
    ['testimonials.reviews.0.name', 'home.blocks.testimonials.elements.1.fullName.content'],
    ['testimonials.reviews.1.quote', 'home.blocks.testimonials.elements.3.shortDescription.content'],
    ['testimonials.reviews.1.name', 'home.blocks.testimonials.elements.4.fullName.content'],
    ['testimonials.reviews.2.quote', 'home.blocks.testimonials.elements.5.shortDescription.content'],
    ['testimonials.reviews.2.name', 'home.blocks.testimonials.elements.6.fullName.content'],

    // === SINGLE TESTIMONIAL — Men Spa: [ fullName, title(body) ] per home.blocks.testimonials.elements ===
    ['testimonial.quote', 'home.blocks.testimonials.elements.1.title.content', undefined, undefined, ['Men Spa']],
    ['testimonial.name', 'home.blocks.testimonials.elements.0.fullName.content', undefined, undefined, ['Men Spa']],
    [
      'testimonial.backgroundImage',
      'home.blocks.testimonials.elements.0.image.url',
      FALLBACK_TYPES.HERO_IMAGE,
      undefined,
      ['Men Spa'],
    ],
    // === SINGLE TESTIMONIAL — Max Fit: [ shortDescription(quote), fullName, … ] ===
    [
      'testimonial.quote',
      'home.blocks.testimonials.elements.0.shortDescription.content',
      undefined,
      undefined,
      ['Max Fit'],
    ],
    [
      'testimonial.name',
      'home.blocks.testimonials.elements.1.fullName.content',
      undefined,
      undefined,
      ['Max Fit'],
    ],
    [
      'testimonial.backgroundImage',
      'home.blocks.testimonials.elements.0.image.url',
      FALLBACK_TYPES.HERO_IMAGE,
      undefined,
      ['Max Fit'],
    ],

    // === SERVICES SECTION (generatePageData `booking.blocks.elements`) ===
    ['services.title', 'booking.blocks.elements.0.title.content'],
    ['services.subtitle', 'booking.blocks.elements.0.shortDescription.content'],
    ['services.description', 'booking.blocks.elements.1.shortDescription.content'],
    ['services.items.0.title', 'booking.blocks.elements.0.title.content'],
    ['services.items.0.description', 'booking.blocks.elements.0.shortDescription.content'],
    ['services.items.0.image', 'booking.blocks.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['services.items.1.title', 'booking.blocks.elements.1.title.content'],
    ['services.items.1.description', 'booking.blocks.elements.1.shortDescription.content'],
    ['services.items.1.image', 'booking.blocks.elements.1.image.url', FALLBACK_TYPES.HERO_IMAGE],

    // === STORE SECTION (generatePageData `store.blocks.elements`) ===
    ['store.title', 'store.blocks.elements.0.title.content'],
    ['store.subtitle', 'store.blocks.elements.0.shortDescription.content'],
    ['store.image', 'store.blocks.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['store.products.0.name', 'store.blocks.elements.0.title.content'],
    ['store.products.0.image', 'store.blocks.elements.0.image.url', FALLBACK_TYPES.STORE_PRODUCT_IMAGE],
    ['store.products.1.name', 'store.blocks.elements.1.title.content'],
    ['store.products.1.image', 'store.blocks.elements.1.image.url', FALLBACK_TYPES.STORE_PRODUCT_IMAGE],
    ['store.products.2.name', 'store.blocks.elements.2.title.content'],
    ['store.products.2.image', 'store.blocks.elements.2.image.url', FALLBACK_TYPES.STORE_PRODUCT_IMAGE],

    // === NEWSLETTER SECTION (Max Fit — contact2: title on element 1, copy on element 0) ===
    ['newsletter.title', 'home.blocks.contact2.elements.1.title.content'],
    ['newsletter.description', 'home.blocks.contact2.elements.0.shortDescription.content'],
    // Modern / Law-style responses: single footer block (shortDescription and optional title)
    ['newsletter.footerTitle', 'home.blocks.footer.elements.0.title.content'],
    ['newsletter.footerBlurb', 'home.blocks.footer.elements.0.shortDescription.content'],

    // === SITE FOOTER CONTACT (Max Fit, Pet Sitter — accountInfo on home) ===
    ['siteFooter.contact.email', 'home.blocks.accountInfo.email.content'],
    ['siteFooter.contact.phone', 'home.blocks.accountInfo.phone.content'],
    ['siteFooter.visit.address', 'home.blocks.accountInfo.address.content'],

    // === CTA BANNER SECTION (Spa) ===
    ['ctaBanner.title', 'home.blocks.callToAction1.elements.0.title.content'],
    ['ctaBanner.subtitle', 'home.blocks.callToAction1.elements.1.shortDescription.content'],
    ['ctaBanner.backgroundImage', 'home.blocks.callToAction1.elements.2.image.url', FALLBACK_TYPES.HERO_IMAGE],

    // === GALLERY SECTION (Shampoo Conditioner, Spa) ===
    ['gallery.heroImage', 'home.blocks.photo2.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],
    ['gallery.title', 'home.blocks.footer.elements.0.title.content'],
    ['gallery.subtitle', 'home.blocks.social.elements.0.description.content'],
    // Shampoo / social grid — no hero fallback here; missing paths must not overwrite Men Spa’s gallery.gallery.urls
    ['gallery.images.0', 'home.blocks.social.elements.1.image.url'],
    ['gallery.images.1', 'home.blocks.social.elements.2.image.url'],
    ['gallery.images.2', 'home.blocks.social.elements.3.image.url'],
    ['gallery.images.3', 'home.blocks.social.elements.4.image.url'],
    ['gallery.images.4', 'home.blocks.social.elements.5.image.url'],
    ['gallery.images.5', 'home.blocks.social.elements.6.image.url'],
    ['gallery.images.6', 'home.blocks.social.elements.7.image.url'],
    ['gallery.images.7', 'home.blocks.social.elements.8.image.url'],
    // Modern / Men Spa: { gallery: { urls, type, orientation, imagesCount } } under home.blocks.gallery
    ['gallery.images.0', 'home.blocks.gallery.gallery.urls.0'],
    ['gallery.images.1', 'home.blocks.gallery.gallery.urls.1'],
    ['gallery.images.2', 'home.blocks.gallery.gallery.urls.2'],
    ['gallery.images.3', 'home.blocks.gallery.gallery.urls.3'],
    ['gallery.images.4', 'home.blocks.gallery.gallery.urls.4'],
    ['gallery.images.5', 'home.blocks.gallery.gallery.urls.5'],
    ['gallery.images.6', 'home.blocks.gallery.gallery.urls.6'],
    ['gallery.images.7', 'home.blocks.gallery.gallery.urls.7'],
    ['gallery.images.8', 'home.blocks.gallery.gallery.urls.8'],
    ['gallery.images.9', 'home.blocks.gallery.gallery.urls.9'],
    ['gallery.images.10', 'home.blocks.gallery.gallery.urls.10'],
    ['gallery.images.11', 'home.blocks.gallery.gallery.urls.11'],
    // Gallery features (Shampoo Conditioner)
    ['gallery.features.0.title', 'home.blocks.features.elements.0.title.content'],
    ['gallery.features.0.description', 'home.blocks.features.elements.1.description.content'],
    ['gallery.features.1.title', 'home.blocks.features.elements.2.title.content'],
    ['gallery.features.1.description', 'home.blocks.features.elements.3.description.content'],
    ['gallery.features.2.title', 'home.blocks.features.elements.4.title.content'],
    ['gallery.features.2.description', 'home.blocks.features.elements.5.description.content'],
    ['gallery.features.3.title', 'home.blocks.features.elements.6.title.content'],
    ['gallery.features.3.description', 'home.blocks.features.elements.7.description.content'],

    // === HOURS SECTION (Spa) ===
    ['hours.image', 'home.blocks.aboutUs2.elements.0.image.url', FALLBACK_TYPES.HERO_IMAGE],

    // === TEAM SECTION (Modern / Men Spa home) ===
    // elements: [m0 shortDesc, m0 image, m0 fullName, m1 image, m1 shortDesc, m1 fullName, section tagline?]
    ['team.subtitle', 'home.blocks.team.elements.6.shortDescription.content'],
    ['team.members.0.description', 'home.blocks.team.elements.0.shortDescription.content'],
    ['team.members.0.image', 'home.blocks.team.elements.1.image.url'],
    ['team.members.0.name', 'home.blocks.team.elements.2.fullName.content'],
    ['team.members.1.image', 'home.blocks.team.elements.3.image.url'],
    ['team.members.1.description', 'home.blocks.team.elements.4.shortDescription.content'],
    ['team.members.1.name', 'home.blocks.team.elements.5.fullName.content'],
  ],
}

export { FALLBACK_IMAGE }
